import dayjs from "dayjs";
import {
  AiMlServicesTrendPoint,
  ModelServingEndpointTrendPoint,
  UnifiedAIMLEndpointItem,
} from "./types";

export type Granularity = "day" | "week" | "month";

export type MetricType = "cost" | "requests" | "tokens" | "users";

export const SERVICE_COLORS = {
  model_serving: "#3B82F6",
  vector_search: "#10B981",
  ai_gateway: "#8B5CF6",
} as const;

export const METRIC_COLORS: Record<MetricType, string> = {
  cost: "#3B82F6",
  requests: "#10B981",
  tokens: "#8B5CF6",
  users: "#F59E0B",
};

export const GRANULARITY_LABELS: Record<Granularity, string> = {
  day: "Daily",
  week: "Weekly",
  month: "Monthly",
};

export type ServingEntityType =
  | "AI_FUNCTION"
  | "CUSTOM_MODEL"
  | "FOUNDATION_MODEL"
  | "EXTERNAL_MODEL"
  | "FEATURE_SPEC";

// `entity_type` values returned by the unified endpoints list. Includes the
// model_serving entity types plus the synthesized values BE attaches to
// vector_search (`VECTOR_SEARCH`) and ai_gateway (`AI_GATEWAY`) rows.
const ENTITY_TYPE_LABELS: Record<string, string> = {
  AI_FUNCTION: "AI Function",
  CUSTOM_MODEL: "Custom Model",
  FOUNDATION_MODEL: "Foundation Model",
  EXTERNAL_MODEL: "External Model",
  FEATURE_SPEC: "Feature Spec",
  VECTOR_SEARCH: "Vector Search",
  AI_GATEWAY: "AI Gateway",
};

export const getServingTypeLabel = (
  entityType: ServingEntityType | string | null | undefined
): string => ENTITY_TYPE_LABELS[entityType ?? ""] ?? entityType ?? "\u2014";

// Per-type Tailwind palette — chosen so every entity type renders as a
// visible color-coded chip with consistent contrast. `!` (important) is
// required because lego's default Badge variant pre-applies `al-bg-primary`
// / `al-text-primary-foreground`, which otherwise win over plain overrides
// for color names that overlap the theme (blue, green).
const ENTITY_TYPE_BADGE_CLASSES: Record<string, string> = {
  FOUNDATION_MODEL: "!al-bg-green-100 !al-text-green-700 al-border-transparent",
  EXTERNAL_MODEL: "!al-bg-orange-100 !al-text-orange-700 al-border-transparent",
  CUSTOM_MODEL: "!al-bg-blue-100 !al-text-blue-700 al-border-transparent",
  AI_FUNCTION: "!al-bg-purple-100 !al-text-purple-700 al-border-transparent",
  FEATURE_SPEC: "!al-bg-yellow-100 !al-text-yellow-800 al-border-transparent",
  VECTOR_SEARCH: "!al-bg-teal-100 !al-text-teal-700 al-border-transparent",
  AI_GATEWAY: "!al-bg-indigo-100 !al-text-indigo-700 al-border-transparent",
};

const DEFAULT_BADGE_CLASS = "!al-bg-gray-100 !al-text-gray-700 al-border-transparent";

export const getEndpointTypeBadge = (
  item: UnifiedAIMLEndpointItem
): { label: string; className: string } => ({
  label: getServingTypeLabel(item.entity_type),
  className: ENTITY_TYPE_BADGE_CLASSES[item.entity_type] ?? DEFAULT_BADGE_CLASS,
});

const SERVING_TYPE_COLOR_TAG_MAP: Record<string, string> = {
  AI_FUNCTION: "purple",
  CUSTOM_MODEL: "blue",
  FOUNDATION_MODEL: "green",
  EXTERNAL_MODEL: "orange",
  FEATURE_SPEC: "yellow",
};

export const getServingTypeColorTagColor = (
  entityType: ServingEntityType | string | null | undefined
): string => SERVING_TYPE_COLOR_TAG_MAP[entityType ?? ""] ?? "blue";

export const formatDate = (value: string, granularity?: Granularity): string => {
  if (granularity === "month") return dayjs(value).format("MMM YYYY");
  return dayjs(value).format("MMM D");
};

export const formatShortDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const detectGranularity = (data: ModelServingEndpointTrendPoint[]): Granularity => {
  if (data.length < 2) return "day";
  const diff = dayjs(data[1].date).diff(dayjs(data[0].date), "day");
  if (diff >= 20) return "month";
  if (diff >= 5) return "week";
  return "day";
};

export const calculateTotals = (trend: AiMlServicesTrendPoint[]) => {
  return trend.reduce(
    (acc, day) => ({
      total_dbus: acc.total_dbus + day.total_dbus,
      total_cost_usd: acc.total_cost_usd + day.total_cost_usd,
      model_serving_dbus: acc.model_serving_dbus + day.model_serving_dbus,
      model_serving_cost_usd: acc.model_serving_cost_usd + day.model_serving_cost_usd,
      vector_search_dbus: acc.vector_search_dbus + day.vector_search_dbus,
      vector_search_cost_usd: acc.vector_search_cost_usd + day.vector_search_cost_usd,
      ai_gateway_dbus: acc.ai_gateway_dbus + day.ai_gateway_dbus,
      ai_gateway_cost_usd: acc.ai_gateway_cost_usd + day.ai_gateway_cost_usd,
    }),
    {
      total_dbus: 0,
      total_cost_usd: 0,
      model_serving_dbus: 0,
      model_serving_cost_usd: 0,
      vector_search_dbus: 0,
      vector_search_cost_usd: 0,
      ai_gateway_dbus: 0,
      ai_gateway_cost_usd: 0,
    }
  );
};
